<template>
  <el-popover v-model="open"
    placement="bottom"
    :width="sizeCalculated"
    popper-class="nebNewSelect"
    trigger="click"
    :visible-arrow="false"
    @show="onShow"
    @hide="() => { open = false }"
    transition="el-zoom-in-top"
  >
    <!-- SELECT-BUTTON -->
    <div class="nebNewSelect-selected"
      :class="[selectedClass, { open }]"
      :style="selectedStyle"
      slot="reference"
    >
      <span class="nebNewSelect-selected-text"
        v-show="hasValue"
      >{{ selectedText }}</span>

      <span class="nebNewSelect-selected-placeholder"
        v-show="!hasValue"
      >
        {{ prefix ? `${prefix}:` : placeholder }}
      </span>

      <caret-down-icon class="nebNewSelect-selected-openIndicator"
        heigh="8px"
        width="10px"
        :rotate="open ? '-180' : '0'"
        v-show="clearable ? !hasValue : true"
      />

      <span class="nebNewSelect-selected-clearWrapper"
        v-show="clearable && hasValue"
        @click="$emit('clear')"
      >
        X
      </span>
    </div>

    <!-- SELECT-DROPDOWN -->
    <div class="nebNewSelect-dropdown"
      v-loading="loading"
    >
      <div class="nebNewSelect-optionList"
        :style="{ 'min-width': sizeCalculated + 'px' }"
      >
        <div class="nebNewSelect-optionList-option"
          v-for="opt in options"
          :class="[{ 'disabled': opt.disabled }]"
          :key="opt.value"
          @click="evt => onOptionSelect(evt, opt)"
        >
          <span>{{ opt.label }}</span>
        </div>
      </div>
    </div>
  </el-popover>
</template>

<script>
import {
  isUndefined,
  isEmpty,
  isNull,
  isNaN,
  isNil,
  isObjectLike
} from 'lodash'

import CaretDownIcon from '@/components/icons/CaretDown.vue'

export default {
  name: 'nebNewSelect',

  components: {
    CaretDownIcon
  },

  props: {
    /**
     * The value of the select.
     *
     * @type {?(String|Number|String[]|Number[])}
     * @default null
     */
    value: {
      type: [Number, String],
      default: null
    },
    prefix: {
      type: String,
      required: false
    },
    size: {
      type: Number,
      default: 260,
      required: false
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: [String, Number],
      default: 'Enter a value'
    },
    fitToContainer: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    emptyStringIsValue: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    defaultValue: {
      type: [ String, Number ],
      default: null
    }
  },

  data: () => ({
    sizeCalculated: 0,
    open: false
  }),

  computed: {
    prefixLength () {
      return isUndefined(this.prefix) ? 0 : this.prefix.length
    },
    defaultSize () {
      return this.size === 150 ? 150 + this.prefixLength * 4 : this.size
    },
    customSize () {
      return this.size === 150 ? 150 + this.prefixLength * 4 + 50 : this.size
    },
    selectedLabel () {
      return this.valueLabel(this.value)
    },
    selectedText () {
      const pre = this.prefix ? this.prefix : ''
      const elm = this.selectedLabel

      if (!isEmpty(pre)) {
        return `${pre}: ${elm}`
      } else {
        return elm
      }
    },
    hasValue () {
      const exists = !isNull(this.value) &&
        !isUndefined(this.value)

      return this.emptyStringIsValue ? exists : exists && !isEmpty(this.value)
    },
    selectedStyle () {
      if (!this.fitToContainer) {
        return { 'width': this.sizeCalculated + 'px' }
      } else {
        return { 'width': '100%' }
      }
    },
    selectedClass () {
      if (!this.hasValue) {
        return 'placeholder'
      } else if (this.hasValue && this.value === this.defaultValue) {
        return ''
      } else {
        return 'selected'
      }
    }
  },

  methods: {
    onShow () {
      this.open = true
      this.$emit('show')
    },
    onOptionSelect (evt, option) {
      if (option.disabled) return

      this.selectValue(option.value)
      this.open = false
    },
    isOptionSelected (option) {
      return this.value === option.value
    },
    exists (thing) {
      return !isNaN(thing) && !isNil(thing) && (isObjectLike(thing) ? !isEmpty(thing) : true)
    },
    optionValue (option) {
      if (this.exists(option)) {
        return option.value
      }
      return null
    },
    selectValue (value) {
      this.$emit('input', value)
    },
    valueLabel () {
      const option = this.options.find(opt => opt.value === this.value)
      return this.exists(option)
        ? option.label
        : 'unknown'
    }
  },

  watch: {
    selectedText () {
      this.sizeCalculated = this.defaultSize
    }
  }
}
</script>

<style lang="scss" scoped>
.nebNewSelect {
  position: relative;
  text-align: left;
  outline: none;
  box-sizing: border-box;

  &-selected {
    border-radius: 3px;
    border: 1px solid #a0c3d2;
    padding-left: 15px;
    cursor: pointer;
    user-select: none;
    color: #5c5e62;
    position: relative;
    box-sizing: border-box;
    overflow: hidden;
    font-size: 12px;
    height: 34px;
    line-height: 34px;
    outline: none;

    &.selected {
      background-color: #f5fbff;
    }

    &.selected:focus {
      outline: none;
    }

    &.open {
      border-color: #74cfe7;
    }

    &-text {
      margin-right: 30px;
    }

    &-openIndicator {
      position: absolute;
      right: 10px;
      top: 12px;
    }

    &.placeholder {
      color: #9b9b9b;
    }

    &-clearWrapper {
      position: absolute;
      right: 10px;
      top: 0;
    }
  }

  &-dropdown {
    display: flex;
    width: fit-content;
    border: 1px solid #cecece;
    border-radius: 4px;
    background-color: #fff;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
    z-index: 999;
    max-height: 250px;
    overflow-y: scroll;
    overflow-x: hidden;
    box-sizing: border-box;
  }

  &-optionList {
    padding: 5px 0;
    border-right: 1px solid #cecece;

    &-option {
      padding-left: 15px;
      cursor: pointer;
      user-select: none;
      color: #5c5e62;
      line-height: 2.9;
      font-size: 12px;
      white-space: pre-wrap;
      word-break: break-word;

      &:hover {
        background-color: #e8f4fc;
      }

      &.disabled {
        cursor: not-allowed;
      }
    }
  }
  &-calendar {
    display: flex;
    align-items: center;
  }
}
</style>

<style lang="scss">
.nebNewSelect {
  &-calendar {
    a {
      color: unset;
      text-decoration: unset;
      cursor: unset;
    }

    a:hover {
      text-decoration: unset;
    }
  }
  &.el-popper {
    padding: 0;
    background-color: red;
    margin-top: 4px;
  }
}
</style>
